import React, { useEffect, useState, useRef, Children } from 'react';
import { PlusIcon, SearchIcon, EyeIcon, DownloadIcon, FileTextIcon, FileDownIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon, ImageIcon, XIcon, ChevronUpIcon, ChevronDownIcon, EditIcon, ClockIcon, RefreshCwIcon, CheckIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { ClinicalHistory as ClinicalHistoryType } from '../../types';
import { formatDate, getDoctorPrefix } from '../../utils/helpers';
import { exportClinicalHistoriesToExcel } from '../../utils/excelExport';
import { exportClinicalHistoriesToPdf } from '../../utils/pdfExport';
import { generateClinicalHistoryPdf } from '../../utils/clinicalHistoryPdf';
import { FormSection } from '../ui/FormSection';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
export function ClinicalHistory() {
  const {
    patients,
    getPatient,
    doctors,
    clinicalHistories,
    addClinicalHistory,
    updateClinicalHistory,
    appointments,
    services,
    getSpecialty
  } = useData();
  const {
    user
  } = useAuth();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('clinicalHistory');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [editingHistory, setEditingHistory] = useState<ClinicalHistoryType | null>(null);
  const [selectedHistory, setSelectedHistory] = useState<ClinicalHistoryType | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);
  // Retomar historial state
  const [previousHistoryForPatient, setPreviousHistoryForPatient] = useState<ClinicalHistoryType | null>(null);
  const [retakeApplied, setRetakeApplied] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-clinical-history-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | null>(null);
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateClinicalHistory(id, {
      status: 'archived'
    } as any).catch(e => console.error('Error archiving clinical history in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateClinicalHistory(id, {
      status: 'deactivated'
    } as any).catch(e => console.error('Error deactivating clinical history in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateClinicalHistory(id, {
      status: 'active'
    } as any).catch(e => console.error('Error restoring clinical history in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const openActionConfirm = (history: ClinicalHistoryType, type: 'archive' | 'deactivate') => {
    setSelectedHistory(history);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedHistory || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedHistory.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedHistory.id);
    }
  };
  // Find the doctor record that matches the current user (by email)
  const currentUserDoctor = doctors.find(d => d.email?.toLowerCase() === user?.email?.toLowerCase());
  const isDoctor = !!currentUserDoctor && !isAdmin;
  // Load deactivated/archived statuses for related entities
  const [patientStatuses, setPatientStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const ps = localStorage.getItem('savidna-patients-status');
      if (ps) setPatientStatuses(JSON.parse(ps));
      const ds = localStorage.getItem('savidna-doctors-status');
      if (ds) setDoctorStatuses(JSON.parse(ds));
    } catch {}
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-patients-status' && e.newValue) {
        try {
          setPatientStatuses(JSON.parse(e.newValue));
        } catch {}
      }
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isPatientActive = (id: string) => !patientStatuses[id];
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  // Form state - Antecedentes Personales
  const [personalData, setPersonalData] = useState({
    fullName: '',
    birthDate: '',
    address: '',
    phone: '',
    email: '',
    age: 0,
    gender: '',
    occupation: '',
    numberOfChildren: 0,
    deliveryType: '',
    alcohol: '',
    tobacco: '',
    drugs: '',
    infusions: '',
    consultationReason: ''
  });
  // Form state - Antecedentes Clínicos
  const [clinicalData, setClinicalData] = useState({
    diseases: '',
    currentMedications: '',
    chronicDiseases: '',
    surgicalHistory: '',
    implantsOrDevices: '',
    implantType: '',
    allergies: ''
  });
  // Form state - Exploración Física
  const [physicalExamData, setPhysicalExamData] = useState({
    bloodPressure: '',
    bloodSugar: '',
    heartRate: '',
    lungs: '',
    eyes: '',
    throat: '',
    abdomen: '',
    weight: '',
    height: '',
    temperature: '',
    positiveAspects: '',
    respiratoryRate: '',
    weightLb: ''
  });
  // Form state - Historia de la Enfermedad Actual
  const [diseaseHistoryData, setDiseaseHistoryData] = useState({
    sinceWhen: '',
    previousTreatment: '',
    evolution: ''
  });
  // Form state - Revisión por Sistemas
  const [systemsReviewData, setSystemsReviewData] = useState({
    general: '',
    cardioRespiratory: '',
    gastrointestinal: '',
    genitourinary: '',
    osteoMuscular: '',
    neurological: '',
    skinAndAppendages: ''
  });
  // Form state - Diagnóstico, Tratamiento, etc.
  const [diagnosis, setDiagnosis] = useState('');
  const [treatment, setTreatment] = useState('');
  const [medicalIndications, setMedicalIndications] = useState('');
  const [selectedSessionServiceIds, setSelectedSessionServiceIds] = useState<string[]>([]);
  const [sessionNumber, setSessionNumber] = useState('');
  const [sessionServiceNumbers, setSessionServiceNumbers] = useState<Record<string, string>>({});
  const [comments, setComments] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [sessionServiceSearch, setSessionServiceSearch] = useState('');
  const [attachedImages, setAttachedImages] = useState<{
    name: string;
    data: string;
  }[]>([]);
  const [viewingImage, setViewingImage] = useState<string | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  // Character limits for print-safe fields (priority order)
  const CHAR_LIMITS = {
    diagnosis: 500,
    treatment: 1000,
    medicalIndications: 500,
    comments: 300
  };
  // Today's date for max restriction
  const today = new Date().toISOString().split('T')[0];
  const filteredHistories = clinicalHistories.filter(h => {
    const patient = getPatient(h.patientId);
    const matchesSearch = patient?.name.toLowerCase().includes(searchTerm.toLowerCase());
    // Status filter
    const status = getItemStatus(h.id);
    const matchesStatus = status === viewMode;
    // If user is a doctor (not admin), only show their own clinical histories
    if (isDoctor && currentUserDoctor) {
      return matchesSearch && matchesStatus && h.doctorId === currentUserDoctor.id;
    }
    return matchesSearch && matchesStatus;
  });
  const {
    sortedItems: sortedHistories,
    requestSort: chRequestSort,
    isSorted: chIsSorted
  } = useSortableData(filteredHistories, {
    key: 'date',
    direction: 'desc'
  });
  // Pagination for clinical histories
  const {
    paginatedItems: paginatedHistories,
    currentPage: chCurrentPage,
    totalPages: chTotalPages,
    totalItems: chTotalItems,
    startIndex: chStartIndex,
    endIndex: chEndIndex,
    goToPage: chGoToPage,
    nextPage: chNextPage,
    prevPage: chPrevPage
  } = usePagination(sortedHistories, {
    itemsPerPage: 10
  });
  // Counts for tabs
  const activeCount = clinicalHistories.filter(h => getItemStatus(h.id) === 'active').length;
  const archivedCount = clinicalHistories.filter(h => getItemStatus(h.id) === 'archived').length;
  const deactivatedCount = clinicalHistories.filter(h => getItemStatus(h.id) === 'deactivated').length;
  // Show ALL active patients (no doctor-based filtering)
  const availablePatients = patients.filter(p => isPatientActive(p.id));
  const patientOptions = availablePatients.map(p => ({
    value: p.id,
    label: p.name
  }));
  // Filter doctors based on selected patient's appointments
  const doctorOptions = doctors.filter(d => {
    if (!isDoctorActive(d.id)) return false;
    if (!selectedPatientId) return true;
    const hasAppointment = appointments.some(a => a.patientId === selectedPatientId && a.doctorId === d.id && a.status === 'completada');
    return hasAppointment;
  }).map(d => ({
    value: d.id,
    label: `${getDoctorPrefix(d.gender, getSpecialty(d.specialtyId)?.name)} ${d.name}`
  }));
  const openNewForm = () => {
    setSelectedHistory(null);
    setSelectedPatientId('');
    setSaveError('');
    setPreviousHistoryForPatient(null);
    setRetakeApplied(false);
    resetForm();
    setIsFormOpen(true);
  };
  const openViewHistory = (history: ClinicalHistoryType) => {
    setSelectedHistory(history);
    setIsViewOpen(true);
  };
  const openEditHistory = (history: ClinicalHistoryType) => {
    setEditingHistory(history);
    setSelectedPatientId(history.patientId);
    setSaveError('');
    // Populate personal background
    setPersonalData({
      fullName: history.personalBackground.fullName || '',
      birthDate: history.personalBackground.birthDate || '',
      address: history.personalBackground.address || '',
      phone: history.personalBackground.phone || '',
      email: history.personalBackground.email || '',
      age: history.personalBackground.age || 0,
      gender: history.personalBackground.gender || '',
      occupation: history.personalBackground.occupation || '',
      numberOfChildren: history.personalBackground.numberOfChildren || 0,
      deliveryType: history.personalBackground.deliveryType || '',
      alcohol: history.personalBackground.alcohol || '',
      tobacco: history.personalBackground.tobacco || '',
      drugs: history.personalBackground.drugs || '',
      infusions: history.personalBackground.infusions || '',
      consultationReason: history.personalBackground.consultationReason || ''
    });
    // Populate clinical background
    setClinicalData({
      diseases: history.clinicalBackground.diseases || '',
      currentMedications: history.clinicalBackground.currentMedications || '',
      chronicDiseases: history.clinicalBackground.chronicDiseases || '',
      surgicalHistory: history.clinicalBackground.surgicalHistory || '',
      implantsOrDevices: history.clinicalBackground.implantsOrDevices || '',
      implantType: history.clinicalBackground.implantType || '',
      allergies: history.clinicalBackground.allergies || ''
    });
    // Populate physical exam
    setPhysicalExamData({
      bloodPressure: history.physicalExam?.bloodPressure || '',
      bloodSugar: history.physicalExam?.bloodSugar || '',
      heartRate: history.physicalExam?.heartRate || '',
      lungs: history.physicalExam?.lungs || '',
      eyes: history.physicalExam?.eyes || '',
      throat: history.physicalExam?.throat || '',
      abdomen: history.physicalExam?.abdomen || '',
      weight: history.physicalExam?.weight || '',
      height: history.physicalExam?.height || '',
      temperature: history.physicalExam?.temperature || '',
      positiveAspects: (history.physicalExam as any)?.positiveAspects || '',
      respiratoryRate: (history.physicalExam as any)?.respiratoryRate || '',
      weightLb: (history.physicalExam as any)?.weightLb || ''
    });
    setDiseaseHistoryData({
      sinceWhen: (history as any).diseaseHistory?.sinceWhen || '',
      previousTreatment: (history as any).diseaseHistory?.previousTreatment || '',
      evolution: (history as any).diseaseHistory?.evolution || ''
    });
    setSystemsReviewData({
      general: (history as any).systemsReview?.general || '',
      cardioRespiratory: (history as any).systemsReview?.cardioRespiratory || '',
      gastrointestinal: (history as any).systemsReview?.gastrointestinal || '',
      genitourinary: (history as any).systemsReview?.genitourinary || '',
      osteoMuscular: (history as any).systemsReview?.osteoMuscular || '',
      neurological: (history as any).systemsReview?.neurological || '',
      skinAndAppendages: (history as any).systemsReview?.skinAndAppendages || ''
    });
    setDiagnosis(history.diagnosis || '');
    setTreatment(history.treatment || '');
    setMedicalIndications(history.medicalIndications || '');
    setComments(history.comments || '');
    // Populate session services
    const svcIds = (history.sessionServices || []).map(ss => ss.serviceId);
    setSelectedSessionServiceIds(svcIds);
    const nums: Record<string, string> = {};
    (history.sessionServices || []).forEach(ss => {
      nums[ss.serviceId] = ss.sessionNumber || '1';
    });
    setSessionServiceNumbers(nums);
    setAttachedImages(history.attachedImages || []);
    setFormErrors({});
    setSessionServiceSearch('');
    setIsEditFormOpen(true);
  };
  const resetForm = () => {
    setPersonalData({
      fullName: '',
      birthDate: '',
      address: '',
      phone: '',
      email: '',
      age: 0,
      gender: '',
      occupation: '',
      numberOfChildren: 0,
      deliveryType: '',
      alcohol: '',
      tobacco: '',
      drugs: '',
      infusions: '',
      consultationReason: ''
    });
    setClinicalData({
      diseases: '',
      currentMedications: '',
      chronicDiseases: '',
      surgicalHistory: '',
      implantsOrDevices: '',
      implantType: '',
      allergies: ''
    });
    setPhysicalExamData({
      bloodPressure: '',
      bloodSugar: '',
      heartRate: '',
      lungs: '',
      eyes: '',
      throat: '',
      abdomen: '',
      weight: '',
      height: '',
      temperature: '',
      positiveAspects: '',
      respiratoryRate: '',
      weightLb: ''
    });
    setDiseaseHistoryData({
      sinceWhen: '',
      previousTreatment: '',
      evolution: ''
    });
    setSystemsReviewData({
      general: '',
      cardioRespiratory: '',
      gastrointestinal: '',
      genitourinary: '',
      osteoMuscular: '',
      neurological: '',
      skinAndAppendages: ''
    });
    setDiagnosis('');
    setTreatment('');
    setMedicalIndications('');
    setSelectedSessionServiceIds([]);
    setSessionNumber('');
    setSessionServiceNumbers({});
    setComments('');
    setFormErrors({});
    setSessionServiceSearch('');
    setAttachedImages([]);
    setViewingImage(null);
  };
  const handlePatientSelect = (patientId: string) => {
    setSelectedPatientId(patientId);
    setRetakeApplied(false);
    const patient = getPatient(patientId);
    if (patient) {
      // If patient is male, set delivery type to N/A automatically
      const isMale = patient.gender?.toLowerCase() === 'masculino';
      setPersonalData({
        fullName: patient.name,
        birthDate: '',
        address: patient.address,
        phone: patient.phone,
        email: patient.email,
        age: 0,
        gender: patient.gender,
        occupation: '',
        numberOfChildren: 0,
        deliveryType: isMale ? 'N/A' : '',
        alcohol: '',
        tobacco: '',
        drugs: '',
        infusions: '',
        consultationReason: ''
      });
    }
    // Find most recent clinical history for this patient
    const patientHistories = clinicalHistories.filter(h => h.patientId === patientId).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setPreviousHistoryForPatient(patientHistories[0] || null);
  };
  // Pre-fill form with data from previous history (antecedentes only, not visit-specific fields)
  const handleRetakeHistory = () => {
    if (!previousHistoryForPatient) return;
    const h = previousHistoryForPatient;
    setPersonalData({
      fullName: h.personalBackground.fullName || '',
      birthDate: h.personalBackground.birthDate || '',
      address: h.personalBackground.address || '',
      phone: h.personalBackground.phone || '',
      email: h.personalBackground.email || '',
      age: h.personalBackground.age || 0,
      gender: h.personalBackground.gender || '',
      occupation: h.personalBackground.occupation || '',
      numberOfChildren: h.personalBackground.numberOfChildren || 0,
      deliveryType: h.personalBackground.deliveryType || '',
      alcohol: h.personalBackground.alcohol || '',
      tobacco: h.personalBackground.tobacco || '',
      drugs: h.personalBackground.drugs || '',
      infusions: h.personalBackground.infusions || '',
      consultationReason: '' // leave blank — new visit reason
    });
    setClinicalData({
      diseases: h.clinicalBackground.diseases || '',
      currentMedications: h.clinicalBackground.currentMedications || '',
      chronicDiseases: h.clinicalBackground.chronicDiseases || '',
      surgicalHistory: h.clinicalBackground.surgicalHistory || '',
      implantsOrDevices: h.clinicalBackground.implantsOrDevices || '',
      implantType: h.clinicalBackground.implantType || '',
      allergies: h.clinicalBackground.allergies || ''
    });
    setPhysicalExamData({
      bloodPressure: '',
      bloodSugar: '',
      heartRate: '',
      lungs: '',
      eyes: '',
      throat: '',
      abdomen: '',
      weight: (h.physicalExam as any)?.weight || '',
      height: (h.physicalExam as any)?.height || '',
      temperature: '',
      positiveAspects: '',
      respiratoryRate: '',
      weightLb: (h.physicalExam as any)?.weightLb || ''
    });
    setDiseaseHistoryData({
      sinceWhen: (h as any).diseaseHistory?.sinceWhen || '',
      previousTreatment: (h as any).diseaseHistory?.previousTreatment || '',
      evolution: '' // leave blank — new evolution
    });
    setSystemsReviewData({
      general: (h as any).systemsReview?.general || '',
      cardioRespiratory: (h as any).systemsReview?.cardioRespiratory || '',
      gastrointestinal: (h as any).systemsReview?.gastrointestinal || '',
      genitourinary: (h as any).systemsReview?.genitourinary || '',
      osteoMuscular: (h as any).systemsReview?.osteoMuscular || '',
      neurological: (h as any).systemsReview?.neurological || '',
      skinAndAppendages: (h as any).systemsReview?.skinAndAppendages || ''
    });
    setRetakeApplied(true);
    // Scroll to top so user sees the form is populated
    setTimeout(() => {
      const modal = document.querySelector('.modal-content-scrollable');
      if (modal) modal.scrollTop = 0;
    }, 100);
  };
  const scrollToTop = () => {
    // Try to scroll the modal content
    const modal = document.querySelector('.modal-content-scrollable');
    if (modal) {
      modal.scrollTop = 0;
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const scrollToFirstError = (errors: Record<string, string>) => {
    // Map error keys to DOM element selectors
    const errorFieldMap: Record<string, string> = {
      patient: '[data-field="patient"]',
      fullName: '[data-field="fullName"]',
      doctor: '[data-field="doctor"]'
    };
    for (const key of Object.keys(errors)) {
      const selector = errorFieldMap[key];
      if (selector) {
        const element = document.querySelector(selector);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          // Add a brief highlight effect
          element.classList.add('ring-2', 'ring-red-400', 'ring-offset-2', 'rounded-lg');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-red-400', 'ring-offset-2', 'rounded-lg');
          }, 3000);
          return;
        }
      }
    }
    // Fallback: scroll to top of modal
    const modal = document.querySelector('.modal-content-scrollable');
    if (modal) {
      modal.scrollTop = 0;
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!selectedPatientId) errors.patient = 'Debe seleccionar un paciente';
    if (!personalData.fullName) errors.fullName = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSaveError('Por favor complete los campos requeridos marcados en rojo.');
      setTimeout(() => scrollToFirstError(errors), 100);
      return;
    }
    setIsSaving(true);
    setSaveError('');
    // Auto-assign doctor from current user
    const autoDoctor = currentUserDoctor;
    const autoDoctorId = autoDoctor?.id || user?.id || '';
    const autoDoctorName = autoDoctor?.name || user?.name || '';
    const now = new Date();
    const localDateStr = now.toLocaleDateString('en-CA', {
      timeZone: 'America/Santo_Domingo'
    });
    const localDate = new Date(localDateStr + 'T12:00:00');
    // Build sessions string from selected services
    const sessionsText = selectedSessionServiceIds.map(id => services.find(s => s.id === id)?.name).filter(Boolean).join(', ');
    // Build per-service session data
    const sessionServicesData = selectedSessionServiceIds.map(id => {
      const svc = services.find(s => s.id === id);
      return svc ? {
        serviceId: id,
        serviceName: svc.name,
        sessionNumber: sessionServiceNumbers[id] || '1'
      } : null;
    }).filter(Boolean) as {
      serviceId: string;
      serviceName: string;
      sessionNumber: string;
    }[];
    try {
      const savedHistory = await addClinicalHistory({
        patientId: selectedPatientId,
        date: localDate,
        doctorId: autoDoctorId,
        doctorName: autoDoctorName,
        personalBackground: personalData,
        clinicalBackground: clinicalData,
        physicalExam: physicalExamData,
        diseaseHistory: diseaseHistoryData,
        systemsReview: systemsReviewData,
        diagnosis,
        treatment,
        medicalIndications,
        sessions: sessionsText,
        sessionNumber,
        sessionServices: sessionServicesData,
        comments,
        doctorSignature: autoDoctor ? `${getDoctorPrefix(autoDoctor.gender, getSpecialty(autoDoctor.specialtyId)?.name)} ${autoDoctor.name}` : user?.name || '',
        attachedImages: attachedImages
      } as any);
      // If images were attached, auto-generate PDF with images
      if (attachedImages.length > 0) {
        try {
          const patient = getPatient(selectedPatientId);
          await generateClinicalHistoryPdf(savedHistory, patient?.name, attachedImages);
        } catch (pdfErr) {
          console.error('Error generando PDF con imágenes:', pdfErr);
        }
      }
      setIsFormOpen(false);
      resetForm();
    } catch (error: any) {
      console.error('❌ Error guardando historial clínico:', error);
      setSaveError(error?.message || 'Error al guardar el historial clínico. Verifica la consola para más detalles.');
      const modal = document.querySelector('.modal-content-scrollable');
      if (modal) {
        modal.scrollTop = 0;
      }
    } finally {
      setIsSaving(false);
    }
  };
  const handlePrint = () => {
    // Add a small delay to ensure styles are applied
    setTimeout(() => {
      window.print();
    }, 100);
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = clinicalHistories;
    if (dateFrom && dateTo) {
      dataToExport = clinicalHistories.filter(h => {
        const dateStr = new Date(h.date).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportClinicalHistoriesToExcel(dataToExport, (patientId: string) => {
      const patient = getPatient(patientId);
      return patient?.name || '-';
    });
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = clinicalHistories;
    if (dateFrom && dateTo) {
      dataToExport = clinicalHistories.filter(h => {
        const dateStr = new Date(h.date).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportClinicalHistoriesToPdf(dataToExport, (patientId: string) => getPatient(patientId)?.name || '-');
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const handleDownloadPdf = async (history: ClinicalHistoryType) => {
    setIsGeneratingPdf(true);
    try {
      const patient = getPatient(history.patientId);
      // Pass saved attached images from the history record
      await generateClinicalHistoryPdf(history, patient?.name, history.attachedImages);
    } catch (error) {
      console.error('Error generando PDF:', error);
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // Handle image attachment
  const handleImageAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`La imagen "${file.name}" es demasiado grande. Máximo 10MB.`);
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert(`El archivo "${file.name}" no es una imagen válida.`);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachedImages(prev => [...prev, {
          name: file.name,
          data: reader.result as string
        }]);
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be selected again
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  // Available services for session combobox
  const filteredSessionServices = services.filter(s => sessionServiceSearch ? s.name.toLowerCase().includes(sessionServiceSearch.toLowerCase()) : true);
  // Toggle session service selection
  const toggleSessionService = (serviceId: string) => {
    setSelectedSessionServiceIds(prev => {
      if (prev.includes(serviceId)) {
        // Remove service - also clean up its session number
        setSessionServiceNumbers(nums => {
          const next = {
            ...nums
          };
          delete next[serviceId];
          return next;
        });
        return prev.filter(id => id !== serviceId);
      } else {
        // Add service - initialize session number to "1"
        setSessionServiceNumbers(nums => ({
          ...nums,
          [serviceId]: '1'
        }));
        return [...prev, serviceId];
      }
    });
  };
  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingHistory) return;
    const errors: Record<string, string> = {};
    if (!selectedPatientId) errors.patient = 'Debe seleccionar un paciente';
    if (!personalData.fullName) errors.fullName = 'El nombre es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSaveError('Por favor complete los campos requeridos marcados en rojo.');
      return;
    }
    setIsSaving(true);
    setSaveError('');
    const sessionsText = selectedSessionServiceIds.map(id => services.find(s => s.id === id)?.name).filter(Boolean).join(', ');
    const sessionServicesData = selectedSessionServiceIds.map(id => {
      const svc = services.find(s => s.id === id);
      return svc ? {
        serviceId: id,
        serviceName: svc.name,
        sessionNumber: sessionServiceNumbers[id] || '1'
      } : null;
    }).filter(Boolean) as {
      serviceId: string;
      serviceName: string;
      sessionNumber: string;
    }[];
    try {
      await updateClinicalHistory(editingHistory.id, {
        patientId: selectedPatientId,
        personalBackground: personalData,
        clinicalBackground: clinicalData,
        physicalExam: physicalExamData,
        diseaseHistory: diseaseHistoryData,
        systemsReview: systemsReviewData,
        diagnosis,
        treatment,
        medicalIndications,
        sessions: sessionsText,
        sessionServices: sessionServicesData,
        comments,
        attachedImages
      } as any);
      setIsEditFormOpen(false);
      setEditingHistory(null);
      resetForm();
    } catch (error: any) {
      console.error('❌ Error actualizando historial clínico:', error);
      setSaveError(error?.message || 'Error al actualizar el historial clínico.');
    } finally {
      setIsSaving(false);
    }
  };
  return <div className="space-y-6" data-id="element-650">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 p-6 text-white shadow-xl" data-id="element-651">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-652" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-653">
          <div data-id="element-654">
            <div className="flex items-center gap-3 mb-2" data-id="element-655">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-656">
                <FileTextIcon className="w-6 h-6" data-id="element-657" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-658">Historial Clínico</h1>
            </div>
            <p className="text-emerald-100 max-w-xl" data-id="element-659">
              {isDoctor ? 'Mis historiales clínicos — Solo se muestran historiales creados por usted' : 'Gestión de historiales clínicos de pacientes'}
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-660">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-661">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-663" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-662">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-665" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-664">
                Exportar Excel
              </Button>}
            {canCreate('clinicalHistory') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-667" />} onClick={openNewForm} className="bg-white text-emerald-700 hover:bg-emerald-50 shadow-lg shadow-emerald-900/20" data-id="element-666">
                Nuevo Historial
              </Button>}
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-668">
          <LockIcon className="w-4 h-4" data-id="element-669" />
          <span data-id="element-670">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            historiales clínicos.
          </span>
        </div>}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-671">
        <div className="w-full md:w-96 relative" data-id="element-672">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-673" />
          <Input placeholder="Buscar por paciente..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-674" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-675">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-676">
              <tab.icon className="w-4 h-4" data-id="element-677" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-678">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Histories List */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-679">
        <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-id="element-680" />
        <div className="overflow-x-auto" data-id="element-681">
          <table className="w-full" data-id="element-682">
            <thead data-id="element-683">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-684">
                <SortableHeader label="Fecha" sortKey="date" currentDirection={chIsSorted('date')} onSort={chRequestSort} data-id="element-685" />
                <SortableHeader label="Paciente" sortKey="patientId" currentDirection={chIsSorted('patientId')} onSort={chRequestSort} data-id="element-686" />
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-687">
                  Motivo de Consulta
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-688">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-689">
              {filteredHistories.length === 0 ? <tr data-id="element-690">
                  <td colSpan={4} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-691">
                    <div className="flex flex-col items-center justify-center" data-id="element-692">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-693">
                        <FileTextIcon className="w-8 h-8 text-gray-400" data-id="element-694" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-695">
                        No se encontraron historiales
                      </h3>
                      <p className="text-sm" data-id="element-696">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando un nuevo historial' : 'No hay historiales en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedHistories.map(history => {
              const patient = getPatient(history.patientId);
              return <tr key={history.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-697">
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-698">
                        {formatDate(history.date)}
                      </td>
                      <td className="py-4 px-6" data-id="element-699">
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-700">
                          {patient?.name}
                        </p>
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400" data-id="element-701">
                        {history.personalBackground.consultationReason || '-'}
                      </td>
                      <td className="py-4 px-6" data-id="element-702">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-703">
                          <button onClick={() => openViewHistory(history)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver historial" data-id="element-704">
                            <EyeIcon className="w-4 h-4" data-id="element-705" />
                          </button>
                          {viewMode === 'active' && !readOnly && <button onClick={() => openEditHistory(history)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Editar historial" data-id="element-706">
                              <EditIcon className="w-4 h-4" data-id="element-707" />
                            </button>}
                          <button onClick={() => handleDownloadPdf(history)} className="p-2 text-gray-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-708">
                            <FileDownIcon className="w-4 h-4" data-id="element-709" />
                          </button>

                          {viewMode === 'active' ? <>
                              <button onClick={() => openActionConfirm(history, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-710">
                                <ArchiveIcon className="w-4 h-4" data-id="element-711" />
                              </button>
                              <button onClick={() => openActionConfirm(history, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-712">
                                <PowerOffIcon className="w-4 h-4" data-id="element-713" />
                              </button>
                            </> : <button onClick={() => restoreItem(history.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-714">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-715" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      <Pagination currentPage={chCurrentPage} totalPages={chTotalPages} totalItems={chTotalItems} startIndex={chStartIndex} endIndex={chEndIndex} onPageChange={chGoToPage} onNext={chNextPage} onPrev={chPrevPage} data-id="element-716" />

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title="Nuevo Historial Clínico" size="full" icon={<FileTextIcon className="w-6 h-6" data-id="element-718" />} accentColor="emerald" data-id="element-717">
        <div className="modal-content-scrollable" data-id="element-719">
          <form onSubmit={handleSubmit} className="space-y-8 pb-8" data-id="element-720">
            {saveError && <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm" data-id="element-721">
                ❌ {saveError}
              </div>}

            {/* Selección de Paciente */}
            <FormSection title="Paciente" data-id="element-722">
              <div className="grid grid-cols-1 gap-6" data-id="element-723">
                <div data-field="patient" data-id="element-724">
                  <Select label="Seleccionar Paciente" options={patientOptions} value={selectedPatientId} onChange={handlePatientSelect} error={formErrors.patient} searchable placeholder="Buscar paciente..." data-id="element-725" />
                </div>

                {/* Retomar Historial Banner */}
                {selectedPatientId && previousHistoryForPatient && !retakeApplied && <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-xl" data-id="element-726">
                      <div className="flex items-start gap-3" data-id="element-727">
                        <div className="p-2 bg-amber-100 dark:bg-amber-800/50 rounded-lg flex-shrink-0" data-id="element-728">
                          <ClockIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" data-id="element-729" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-730">
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300" data-id="element-731">
                            Este paciente tiene un historial previo
                          </p>
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5" data-id="element-732">
                            Última visita:{' '}
                            <span className="font-medium" data-id="element-733">
                              {formatDate(previousHistoryForPatient.date)}
                            </span>
                            {previousHistoryForPatient.personalBackground.consultationReason && <span className="ml-1" data-id="element-734">
                                —{' '}
                                {previousHistoryForPatient.personalBackground.consultationReason}
                              </span>}
                          </p>
                          <p className="text-xs text-amber-600 dark:text-amber-500 mt-1.5" data-id="element-735">
                            ¿Deseas retomar los antecedentes? Se pre-llenarán:{' '}
                            <span className="font-medium" data-id="element-736">
                              datos personales, antecedentes clínicos, revisión
                              por sistemas
                            </span>
                            . El motivo de consulta, diagnóstico y tratamiento
                            quedarán en blanco para esta nueva visita.
                          </p>
                          <div className="flex items-center gap-2 mt-3" data-id="element-737">
                            <button type="button" onClick={handleRetakeHistory} className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg transition-colors shadow-sm" data-id="element-738">
                              <RefreshCwIcon className="w-3.5 h-3.5" data-id="element-739" />
                              Retomar Antecedentes
                            </button>
                            <button type="button" onClick={() => setPreviousHistoryForPatient(null)} className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-xs font-medium rounded-lg transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/30" data-id="element-740">
                              Comenzar desde cero
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>}

                {/* Retake applied confirmation */}
                {selectedPatientId && retakeApplied && <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl flex items-center gap-3" data-id="element-741">
                    <div className="p-1.5 bg-emerald-100 dark:bg-emerald-800/50 rounded-lg flex-shrink-0" data-id="element-742">
                      <CheckIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" data-id="element-743" />
                    </div>
                    <div className="flex-1" data-id="element-744">
                      <p className="text-sm text-emerald-700 dark:text-emerald-300" data-id="element-745">
                        <span className="font-medium" data-id="element-746">
                          Antecedentes retomados
                        </span>{' '}
                        del historial del{' '}
                        {previousHistoryForPatient ? formatDate(previousHistoryForPatient.date) : ''}
                        . Revisa y actualiza los campos según corresponda.
                      </p>
                    </div>
                    <button type="button" onClick={() => {
                  setRetakeApplied(false);
                  // Reset to fresh patient data (undo the retake)
                  const patient = getPatient(selectedPatientId);
                  if (patient) {
                    const isMale = patient.gender?.toLowerCase() === 'masculino';
                    setPersonalData({
                      fullName: patient.name,
                      birthDate: '',
                      address: patient.address,
                      phone: patient.phone,
                      email: patient.email,
                      age: 0,
                      gender: patient.gender,
                      occupation: '',
                      numberOfChildren: 0,
                      deliveryType: isMale ? 'N/A' : '',
                      alcohol: '',
                      tobacco: '',
                      drugs: '',
                      infusions: '',
                      consultationReason: ''
                    });
                  }
                  setClinicalData({
                    diseases: '',
                    currentMedications: '',
                    chronicDiseases: '',
                    surgicalHistory: '',
                    implantsOrDevices: '',
                    implantType: '',
                    allergies: ''
                  });
                  setPhysicalExamData({
                    bloodPressure: '',
                    bloodSugar: '',
                    heartRate: '',
                    lungs: '',
                    eyes: '',
                    throat: '',
                    abdomen: '',
                    weight: '',
                    height: '',
                    temperature: '',
                    positiveAspects: '',
                    respiratoryRate: '',
                    weightLb: ''
                  });
                  setDiseaseHistoryData({
                    sinceWhen: '',
                    previousTreatment: '',
                    evolution: ''
                  });
                  setSystemsReviewData({
                    general: '',
                    cardioRespiratory: '',
                    gastrointestinal: '',
                    genitourinary: '',
                    osteoMuscular: '',
                    neurological: '',
                    skinAndAppendages: ''
                  });
                }} className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline flex-shrink-0" data-id="element-747">
                      Deshacer
                    </button>
                  </div>}

                {/* Show auto-assigned doctor info */}
                {(currentUserDoctor || user) && <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg" data-id="element-748">
                    <p className="text-sm text-emerald-700 dark:text-emerald-300" data-id="element-749">
                      <span className="font-medium" data-id="element-750">
                        Doctor asignado automáticamente:
                      </span>{' '}
                      {currentUserDoctor ? `${getDoctorPrefix(currentUserDoctor.gender, getSpecialty(currentUserDoctor.specialtyId)?.name)} ${currentUserDoctor.name}` : user?.name || ''}
                    </p>
                  </div>}
              </div>
            </FormSection>

            {/* Antecedentes Personales */}
            <FormSection title="Antecedentes Personales" data-id="element-751">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-752">
                <div data-field="fullName" data-id="element-753">
                  <Input label="Nombre y Apellido" value={personalData.fullName} onChange={e => setPersonalData({
                  ...personalData,
                  fullName: e.target.value
                })} error={formErrors.fullName} readOnly={!!selectedPatientId} className={selectedPatientId ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-754" />
                </div>
                <Input type="date" label="Fecha de Nacimiento (F.N.)" value={personalData.birthDate} onChange={e => {
                const newBirthDate = e.target.value;
                let calculatedAge = 0;
                if (newBirthDate) {
                  const birth = new Date(newBirthDate);
                  const now = new Date();
                  calculatedAge = now.getFullYear() - birth.getFullYear();
                  const m = now.getMonth() - birth.getMonth();
                  if (m < 0 || m === 0 && now.getDate() < birth.getDate()) {
                    calculatedAge--;
                  }
                  if (calculatedAge < 0) calculatedAge = 0;
                }
                setPersonalData({
                  ...personalData,
                  birthDate: newBirthDate,
                  age: calculatedAge
                });
              }} max={today} data-id="element-755" />
                <Input label="Dirección" value={personalData.address} onChange={e => setPersonalData({
                ...personalData,
                address: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.address} className={selectedPatientId && personalData.address ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-756" />
                <Input label="Tel./Cel." value={personalData.phone} onChange={e => setPersonalData({
                ...personalData,
                phone: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.phone} className={selectedPatientId && personalData.phone ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-757" />
                <Input type="email" label="E-mail" value={personalData.email} onChange={e => setPersonalData({
                ...personalData,
                email: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.email} className={selectedPatientId && personalData.email ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-758" />
                <Input type="number" label="Edad" value={personalData.age} readOnly className="bg-gray-100 dark:bg-gray-800 cursor-not-allowed" helperText={personalData.birthDate ? 'Calculada automáticamente' : 'Se calcula al ingresar fecha de nacimiento'} data-id="element-759" />
                <Input label="Sexo" value={personalData.gender} onChange={e => setPersonalData({
                ...personalData,
                gender: e.target.value
              })} readOnly={!!selectedPatientId && !!personalData.gender} className={selectedPatientId && personalData.gender ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-760" />
                <Input label="Ocupación u Oficio" value={personalData.occupation} onChange={e => setPersonalData({
                ...personalData,
                occupation: e.target.value
              })} data-id="element-761" />
                <Input type="number" label="N° de Hijos" value={personalData.numberOfChildren} onChange={e => setPersonalData({
                ...personalData,
                numberOfChildren: parseInt(e.target.value) || 0
              })} data-id="element-762" />
                <Input label="Tipo de Parto" value={personalData.deliveryType} onChange={e => setPersonalData({
                ...personalData,
                deliveryType: e.target.value
              })} placeholder="Ej: C/A, Normal" readOnly={personalData.gender?.toLowerCase() === 'masculino'} className={personalData.gender?.toLowerCase() === 'masculino' ? 'bg-gray-100 dark:bg-gray-800' : ''} data-id="element-763" />
                <Input label="Alcohol" value={personalData.alcohol} onChange={e => setPersonalData({
                ...personalData,
                alcohol: e.target.value
              })} data-id="element-764" />
                <Input label="Tabaco" value={personalData.tobacco} onChange={e => setPersonalData({
                ...personalData,
                tobacco: e.target.value
              })} data-id="element-765" />
                <Input label="Drogas" value={personalData.drugs} onChange={e => setPersonalData({
                ...personalData,
                drugs: e.target.value
              })} data-id="element-766" />
                <Input label="Infusiones" value={personalData.infusions} onChange={e => setPersonalData({
                ...personalData,
                infusions: e.target.value
              })} data-id="element-767" />
                <div className="md:col-span-3" data-id="element-768">
                  <Textarea label="Motivo de la Consulta" value={personalData.consultationReason} onChange={e => setPersonalData({
                  ...personalData,
                  consultationReason: e.target.value
                })} rows={2} data-id="element-769" />
                </div>
              </div>
            </FormSection>

            {/* Antecedentes Clínicos */}
            <FormSection title="Antecedentes Clínicos" data-id="element-770">
              <div className="grid grid-cols-1 gap-4" data-id="element-771">
                <Textarea label="Enfermedades" value={clinicalData.diseases} onChange={e => setClinicalData({
                ...clinicalData,
                diseases: e.target.value
              })} rows={2} data-id="element-772" />
                <Textarea label="Medicamentos que Toma Actualmente" value={clinicalData.currentMedications} onChange={e => setClinicalData({
                ...clinicalData,
                currentMedications: e.target.value
              })} rows={2} data-id="element-773" />
                <Textarea label="Antecedentes Enfermedades Crónicas" value={clinicalData.chronicDiseases} onChange={e => setClinicalData({
                ...clinicalData,
                chronicDiseases: e.target.value
              })} rows={2} data-id="element-774" />
                <Textarea label="Antecedentes Quirúrgicos" value={clinicalData.surgicalHistory} onChange={e => setClinicalData({
                ...clinicalData,
                surgicalHistory: e.target.value
              })} rows={2} data-id="element-775" />
                <div className="grid grid-cols-2 gap-4" data-id="element-776">
                  <Input label="Uso de Implantes o Dispositivos" value={clinicalData.implantsOrDevices} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantsOrDevices: e.target.value
                })} data-id="element-777" />
                  <Input label="Tipo" value={clinicalData.implantType} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantType: e.target.value
                })} data-id="element-778" />
                </div>
                <Textarea label="Alergias" value={clinicalData.allergies} onChange={e => setClinicalData({
                ...clinicalData,
                allergies: e.target.value
              })} rows={2} data-id="element-779" />
              </div>
            </FormSection>

            {/* Exploración Física */}
            <FormSection title="Exploración Física" data-id="element-780">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-781">
                <Input label="Presión Arterial (T.A.)" value={physicalExamData.bloodPressure} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodPressure: e.target.value
              })} placeholder="120/80 mmHg" data-id="element-782" />
                <Input label="Azúcar en Sangre" value={physicalExamData.bloodSugar} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodSugar: e.target.value
              })} placeholder="mg/dL" data-id="element-783" />
                <Input label="Frec. Cardíaca (F.C.)" value={physicalExamData.heartRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                heartRate: e.target.value
              })} placeholder="Vm" data-id="element-784" />
                <Input label="Frec. Respiratoria (F.R.)" value={physicalExamData.respiratoryRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                respiratoryRate: e.target.value
              })} placeholder="r/m" data-id="element-785" />
                <Input label="Peso (Kg)" value={physicalExamData.weight} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weight: e.target.value
              })} placeholder="kg" data-id="element-786" />
                <Input label="Peso (Lb)" value={physicalExamData.weightLb} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weightLb: e.target.value
              })} placeholder="lb" data-id="element-787" />
                <Input label="Altura" value={physicalExamData.height} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                height: e.target.value
              })} placeholder="cm" data-id="element-788" />
                <Input label="Temperatura" value={physicalExamData.temperature} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                temperature: e.target.value
              })} placeholder="°C" data-id="element-789" />
                <Input label="Pulmones" value={physicalExamData.lungs} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                lungs: e.target.value
              })} data-id="element-790" />
                <Input label="Ojos" value={physicalExamData.eyes} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                eyes: e.target.value
              })} data-id="element-791" />
                <Input label="Garganta" value={physicalExamData.throat} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                throat: e.target.value
              })} data-id="element-792" />
                <Input label="Abdomen" value={physicalExamData.abdomen} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                abdomen: e.target.value
              })} data-id="element-793" />
                <div className="col-span-2 sm:col-span-3 md:col-span-4" data-id="element-794">
                  <Textarea label="Aspectos Positivos" value={physicalExamData.positiveAspects} onChange={e => setPhysicalExamData({
                  ...physicalExamData,
                  positiveAspects: e.target.value
                })} rows={2} placeholder="Aspectos positivos del examen físico..." data-id="element-795" />
                </div>
              </div>
            </FormSection>

            {/* Historia de la Enfermedad Actual */}
            <FormSection title="Historia de la Enfermedad Actual" data-id="element-796">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-797">
                <Input label="¿Desde cuándo padece el problema?" value={diseaseHistoryData.sinceWhen} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                sinceWhen: e.target.value
              })} placeholder="Ej: 3 meses, 1 año..." data-id="element-798" />
                <Input label="¿Se ha tratado con anterioridad?" value={diseaseHistoryData.previousTreatment} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                previousTreatment: e.target.value
              })} placeholder="Sí / No / Descripción..." data-id="element-799" />
                <div className="md:col-span-2" data-id="element-800">
                  <Textarea label="Evolución" value={diseaseHistoryData.evolution} onChange={e => setDiseaseHistoryData({
                  ...diseaseHistoryData,
                  evolution: e.target.value
                })} rows={4} placeholder="Descripción de la evolución de la enfermedad actual..." data-id="element-801" />
                </div>
              </div>
            </FormSection>

            {/* Revisión por Sistemas */}
            <FormSection title="Revisión por Sistemas" data-id="element-802">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-803">
                <Input label="General" value={systemsReviewData.general} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                general: e.target.value
              })} data-id="element-804" />
                <Input label="Cardio-Respiratorio" value={systemsReviewData.cardioRespiratory} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                cardioRespiratory: e.target.value
              })} data-id="element-805" />
                <Input label="Gastro-Intestinal" value={systemsReviewData.gastrointestinal} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                gastrointestinal: e.target.value
              })} data-id="element-806" />
                <Input label="Genito-Urinario" value={systemsReviewData.genitourinary} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                genitourinary: e.target.value
              })} data-id="element-807" />
                <Input label="Osteo-Muscular" value={systemsReviewData.osteoMuscular} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                osteoMuscular: e.target.value
              })} data-id="element-808" />
                <Input label="Neurológico" value={systemsReviewData.neurological} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                neurological: e.target.value
              })} data-id="element-809" />
                <Input label="Piel & Anexos" value={systemsReviewData.skinAndAppendages} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                skinAndAppendages: e.target.value
              })} data-id="element-810" />
              </div>
            </FormSection>

            {/* Diagnóstico */}
            <FormSection title="Diagnóstico" data-id="element-811">
              <Textarea value={diagnosis} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.diagnosis) setDiagnosis(e.target.value);
            }} rows={6} placeholder="Escriba el diagnóstico..." maxLength={CHAR_LIMITS.diagnosis} data-id="element-812" />
              <p className={`mt-1 text-xs text-right ${diagnosis.length >= CHAR_LIMITS.diagnosis ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-813">
                {diagnosis.length}/{CHAR_LIMITS.diagnosis} caracteres
              </p>
            </FormSection>

            {/* Tratamiento */}
            <FormSection title="Tratamiento" data-id="element-814">
              <Textarea value={treatment} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.treatment) setTreatment(e.target.value);
            }} rows={10} placeholder="Escriba el tratamiento detallado..." maxLength={CHAR_LIMITS.treatment} data-id="element-815" />
              <p className={`mt-1 text-xs text-right ${treatment.length >= CHAR_LIMITS.treatment ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-816">
                {treatment.length}/{CHAR_LIMITS.treatment} caracteres
              </p>
            </FormSection>

            {/* Indicaciones Médicas */}
            <FormSection title="Indicaciones Médicas" data-id="element-817">
              <Textarea value={medicalIndications} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.medicalIndications) setMedicalIndications(e.target.value);
            }} rows={6} placeholder="Escriba las indicaciones médicas para el paciente..." maxLength={CHAR_LIMITS.medicalIndications} data-id="element-818" />
              <p className={`mt-1 text-xs text-right ${medicalIndications.length >= CHAR_LIMITS.medicalIndications ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-819">
                {medicalIndications.length}/{CHAR_LIMITS.medicalIndications}{' '}
                caracteres
              </p>
            </FormSection>

            {/* Sesiones */}
            <FormSection title="Sesiones" data-id="element-820">
              <div className="space-y-4" data-id="element-821">
                {/* Multi-select services for sessions */}
                <div data-id="element-822">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-823">
                    Servicios / Tipo de Sesión
                  </label>

                  {/* Search input */}
                  <div className="relative" data-id="element-824">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-825" />
                    <input type="text" value={sessionServiceSearch} onChange={e => setSessionServiceSearch(e.target.value)} placeholder="Buscar servicios..." className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-id="element-826" />
                  </div>

                  {/* Service list with checkboxes */}
                  <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-827">
                    {filteredSessionServices.length === 0 ? <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-828">
                        No se encontraron servicios
                      </div> : filteredSessionServices.map(svc => {
                    const isSelected = selectedSessionServiceIds.includes(svc.id);
                    return <label key={svc.id} className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-white dark:hover:bg-gray-800'}`} data-id="element-829">
                            <input type="checkbox" checked={isSelected} onChange={() => toggleSessionService(svc.id)} className="w-3.5 h-3.5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500" data-id="element-830" />
                            <span className="text-sm text-gray-900 dark:text-white flex-1" data-id="element-831">
                              {svc.name}
                            </span>
                            <span className="text-xs text-gray-400" data-id="element-832">
                              {svc.duration} min
                            </span>
                          </label>;
                  })}
                  </div>
                </div>

                {/* Per-service session numbers */}
                {selectedSessionServiceIds.length > 0 && <div data-id="element-833">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-834">
                      N° de Sesión por Servicio
                    </label>
                    <div className="space-y-2" data-id="element-835">
                      {selectedSessionServiceIds.map(sId => {
                    const svc = services.find(s => s.id === sId);
                    if (!svc) return null;
                    return <div key={sId} className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg" data-id="element-836">
                            <div className="flex-1 min-w-0" data-id="element-837">
                              <span className="text-sm font-medium text-gray-900 dark:text-white truncate block" data-id="element-838">
                                {svc.name}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-839">
                                {svc.duration} min
                              </span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-840">
                              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" data-id="element-841">
                                Sesión N°
                              </span>
                              <div className="flex items-center" data-id="element-842">
                                <input type="number" min="1" step="1" value={sessionServiceNumbers[sId] || '1'} onChange={e => setSessionServiceNumbers(prev => ({
                            ...prev,
                            [sId]: e.target.value
                          }))} className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-l-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" data-id="element-843" />
                                <div className="flex flex-col border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-lg overflow-hidden" data-id="element-844">
                                  <button type="button" onClick={() => setSessionServiceNumbers(prev => ({
                              ...prev,
                              [sId]: String((parseInt(prev[sId] || '1') || 0) + 1)
                            }))} className="px-1.5 py-0.5 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors border-b border-gray-300 dark:border-gray-600" data-id="element-845">
                                    <ChevronUpIcon className="w-3 h-3" data-id="element-846" />
                                  </button>
                                  <button type="button" onClick={() => setSessionServiceNumbers(prev => ({
                              ...prev,
                              [sId]: String(Math.max(1, (parseInt(prev[sId] || '2') || 2) - 1))
                            }))} className="px-1.5 py-0.5 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 transition-colors" data-id="element-847">
                                    <ChevronDownIcon className="w-3 h-3" data-id="element-848" />
                                  </button>
                                </div>
                              </div>
                              <button type="button" onClick={() => toggleSessionService(sId)} className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors" title="Quitar servicio" data-id="element-849">
                                <XIcon className="w-4 h-4" data-id="element-850" />
                              </button>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>}
              </div>
            </FormSection>

            {/* Comentarios */}
            <FormSection title="Comentarios" data-id="element-851">
              <Textarea value={comments} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.comments) setComments(e.target.value);
            }} rows={3} placeholder="Comentarios adicionales..." maxLength={CHAR_LIMITS.comments} data-id="element-852" />
              <p className={`mt-1 text-xs text-right ${comments.length >= CHAR_LIMITS.comments ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-853">
                {comments.length}/{CHAR_LIMITS.comments} caracteres
              </p>
            </FormSection>

            {/* Imágenes Adjuntas */}
            <FormSection title="Imágenes Adjuntas" data-id="element-854">
              <div className="space-y-4" data-id="element-855">
                <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleImageAttach} className="hidden" data-id="element-856" />
                <button type="button" onClick={() => imageInputRef.current?.click()} className="flex items-center gap-3 w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-emerald-400 dark:hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-all text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400" data-id="element-857">
                  <ImageIcon className="w-6 h-6" data-id="element-858" />
                  <div className="text-left" data-id="element-859">
                    <p className="text-sm font-medium" data-id="element-860">Adjuntar Imágenes</p>
                    <p className="text-xs" data-id="element-861">
                      Click para seleccionar archivos (máx. 10MB cada uno)
                    </p>
                  </div>
                </button>

                {/* Image previews */}
                {attachedImages.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-862">
                    {attachedImages.map((img, index) => <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-863">
                        <img src={img.data} alt={img.name} className="w-full h-48 object-cover cursor-pointer hover:opacity-90 transition-opacity" onClick={() => setViewingImage(img.data)} data-id="element-864" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2" data-id="element-865">
                          <p className="text-xs text-white truncate" data-id="element-866">
                            {img.name}
                          </p>
                        </div>
                        <button type="button" onClick={() => setAttachedImages(prev => prev.filter((_, i) => i !== index))} className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600" data-id="element-867">
                          <XIcon className="w-3 h-3" data-id="element-868" />
                        </button>
                      </div>)}
                  </div>}
              </div>
            </FormSection>

            {/* Firma del Doctor */}
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-869">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2" data-id="element-870">
                Firma del Dr. (a)
              </p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-871">
                {currentUserDoctor ? `${getDoctorPrefix(currentUserDoctor.gender, getSpecialty(currentUserDoctor.specialtyId)?.name)} ${currentUserDoctor.name}` : user?.name || 'Usuario actual'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-872">
                Fecha: {formatDate(new Date())}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800 p-4 -mx-4 -mb-4 shadow-lg" data-id="element-873">
              <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-874">
                Cancelar
              </Button>
              <Button type="submit" isLoading={isSaving} data-id="element-875">
                {isSaving ? 'Guardando...' : 'Guardar Historial Clínico'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Edit Clinical History Modal */}
      <Modal isOpen={isEditFormOpen} onClose={() => {
      setIsEditFormOpen(false);
      setEditingHistory(null);
      resetForm();
    }} title="Editar Historial Clínico" size="full" icon={<EditIcon className="w-6 h-6" data-id="element-877" />} accentColor="emerald" data-id="element-876">
        <div className="modal-content-scrollable" data-id="element-878">
          <form onSubmit={handleEditSubmit} className="space-y-8 pb-8" data-id="element-879">
            {saveError && <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm" data-id="element-880">
                ❌ {saveError}
              </div>}

            {/* Antecedentes Personales */}
            <FormSection title="Antecedentes Personales" data-id="element-881">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-882">
                <div data-field="fullName" data-id="element-883">
                  <Input label="Nombre y Apellido" value={personalData.fullName} onChange={e => setPersonalData({
                  ...personalData,
                  fullName: e.target.value
                })} error={formErrors.fullName} data-id="element-884" />
                </div>
                <Input type="date" label="Fecha de Nacimiento" value={personalData.birthDate} onChange={e => {
                const nb = e.target.value;
                let age = 0;
                if (nb) {
                  const b = new Date(nb);
                  const n = new Date();
                  age = n.getFullYear() - b.getFullYear();
                  const m = n.getMonth() - b.getMonth();
                  if (m < 0 || m === 0 && n.getDate() < b.getDate()) age--;
                  if (age < 0) age = 0;
                }
                setPersonalData({
                  ...personalData,
                  birthDate: nb,
                  age
                });
              }} max={today} data-id="element-885" />
                <Input label="Dirección" value={personalData.address} onChange={e => setPersonalData({
                ...personalData,
                address: e.target.value
              })} data-id="element-886" />
                <Input label="Tel./Cel." value={personalData.phone} onChange={e => setPersonalData({
                ...personalData,
                phone: e.target.value
              })} data-id="element-887" />
                <Input type="email" label="E-mail" value={personalData.email} onChange={e => setPersonalData({
                ...personalData,
                email: e.target.value
              })} data-id="element-888" />
                <Input type="number" label="Edad" value={personalData.age} readOnly className="bg-gray-100 dark:bg-gray-800" data-id="element-889" />
                <Input label="Sexo" value={personalData.gender} onChange={e => setPersonalData({
                ...personalData,
                gender: e.target.value
              })} data-id="element-890" />
                <Input label="Ocupación" value={personalData.occupation} onChange={e => setPersonalData({
                ...personalData,
                occupation: e.target.value
              })} data-id="element-891" />
                <Input type="number" label="N° de Hijos" value={personalData.numberOfChildren} onChange={e => setPersonalData({
                ...personalData,
                numberOfChildren: parseInt(e.target.value) || 0
              })} data-id="element-892" />
                <Input label="Tipo de Parto" value={personalData.deliveryType} onChange={e => setPersonalData({
                ...personalData,
                deliveryType: e.target.value
              })} placeholder="Ej: C/A, Normal" data-id="element-893" />
                <Input label="Alcohol" value={personalData.alcohol} onChange={e => setPersonalData({
                ...personalData,
                alcohol: e.target.value
              })} data-id="element-894" />
                <Input label="Tabaco" value={personalData.tobacco} onChange={e => setPersonalData({
                ...personalData,
                tobacco: e.target.value
              })} data-id="element-895" />
                <Input label="Drogas" value={personalData.drugs} onChange={e => setPersonalData({
                ...personalData,
                drugs: e.target.value
              })} data-id="element-896" />
                <Input label="Infusiones" value={personalData.infusions} onChange={e => setPersonalData({
                ...personalData,
                infusions: e.target.value
              })} data-id="element-897" />
                <div className="md:col-span-3" data-id="element-898">
                  <Textarea label="Motivo de la Consulta" value={personalData.consultationReason} onChange={e => setPersonalData({
                  ...personalData,
                  consultationReason: e.target.value
                })} rows={2} data-id="element-899" />
                </div>
              </div>
            </FormSection>

            {/* Antecedentes Clínicos */}
            <FormSection title="Antecedentes Clínicos" data-id="element-900">
              <div className="grid grid-cols-1 gap-4" data-id="element-901">
                <Textarea label="Enfermedades" value={clinicalData.diseases} onChange={e => setClinicalData({
                ...clinicalData,
                diseases: e.target.value
              })} rows={2} data-id="element-902" />
                <Textarea label="Medicamentos Actuales" value={clinicalData.currentMedications} onChange={e => setClinicalData({
                ...clinicalData,
                currentMedications: e.target.value
              })} rows={2} data-id="element-903" />
                <Textarea label="Enfermedades Crónicas" value={clinicalData.chronicDiseases} onChange={e => setClinicalData({
                ...clinicalData,
                chronicDiseases: e.target.value
              })} rows={2} data-id="element-904" />
                <Textarea label="Antecedentes Quirúrgicos" value={clinicalData.surgicalHistory} onChange={e => setClinicalData({
                ...clinicalData,
                surgicalHistory: e.target.value
              })} rows={2} data-id="element-905" />
                <div className="grid grid-cols-2 gap-4" data-id="element-906">
                  <Input label="Implantes o Dispositivos" value={clinicalData.implantsOrDevices} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantsOrDevices: e.target.value
                })} data-id="element-907" />
                  <Input label="Tipo" value={clinicalData.implantType} onChange={e => setClinicalData({
                  ...clinicalData,
                  implantType: e.target.value
                })} data-id="element-908" />
                </div>
                <Textarea label="Alergias" value={clinicalData.allergies} onChange={e => setClinicalData({
                ...clinicalData,
                allergies: e.target.value
              })} rows={2} data-id="element-909" />
              </div>
            </FormSection>

            {/* Exploración Física */}
            <FormSection title="Exploración Física" data-id="element-910">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-911">
                <Input label="Presión Arterial (T.A.)" value={physicalExamData.bloodPressure} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodPressure: e.target.value
              })} placeholder="120/80 mmHg" data-id="element-912" />
                <Input label="Azúcar en Sangre" value={physicalExamData.bloodSugar} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                bloodSugar: e.target.value
              })} placeholder="mg/dL" data-id="element-913" />
                <Input label="Frec. Cardíaca (F.C.)" value={physicalExamData.heartRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                heartRate: e.target.value
              })} placeholder="Vm" data-id="element-914" />
                <Input label="Frec. Respiratoria (F.R.)" value={physicalExamData.respiratoryRate} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                respiratoryRate: e.target.value
              })} placeholder="r/m" data-id="element-915" />
                <Input label="Peso (Kg)" value={physicalExamData.weight} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weight: e.target.value
              })} placeholder="kg" data-id="element-916" />
                <Input label="Peso (Lb)" value={physicalExamData.weightLb} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                weightLb: e.target.value
              })} placeholder="lb" data-id="element-917" />
                <Input label="Altura" value={physicalExamData.height} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                height: e.target.value
              })} placeholder="cm" data-id="element-918" />
                <Input label="Temperatura" value={physicalExamData.temperature} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                temperature: e.target.value
              })} placeholder="°C" data-id="element-919" />
                <Input label="Pulmones" value={physicalExamData.lungs} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                lungs: e.target.value
              })} data-id="element-920" />
                <Input label="Ojos" value={physicalExamData.eyes} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                eyes: e.target.value
              })} data-id="element-921" />
                <Input label="Garganta" value={physicalExamData.throat} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                throat: e.target.value
              })} data-id="element-922" />
                <Input label="Abdomen" value={physicalExamData.abdomen} onChange={e => setPhysicalExamData({
                ...physicalExamData,
                abdomen: e.target.value
              })} data-id="element-923" />
                <div className="col-span-2 sm:col-span-3 md:col-span-4" data-id="element-924">
                  <Textarea label="Aspectos Positivos" value={physicalExamData.positiveAspects} onChange={e => setPhysicalExamData({
                  ...physicalExamData,
                  positiveAspects: e.target.value
                })} rows={2} placeholder="Aspectos positivos del examen físico..." data-id="element-925" />
                </div>
              </div>
            </FormSection>

            {/* Historia de la Enfermedad Actual */}
            <FormSection title="Historia de la Enfermedad Actual" data-id="element-926">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-927">
                <Input label="¿Desde cuándo padece el problema?" value={diseaseHistoryData.sinceWhen} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                sinceWhen: e.target.value
              })} placeholder="Ej: 3 meses, 1 año..." data-id="element-928" />
                <Input label="¿Se ha tratado con anterioridad?" value={diseaseHistoryData.previousTreatment} onChange={e => setDiseaseHistoryData({
                ...diseaseHistoryData,
                previousTreatment: e.target.value
              })} placeholder="Sí / No / Descripción..." data-id="element-929" />
                <div className="md:col-span-2" data-id="element-930">
                  <Textarea label="Evolución" value={diseaseHistoryData.evolution} onChange={e => setDiseaseHistoryData({
                  ...diseaseHistoryData,
                  evolution: e.target.value
                })} rows={4} placeholder="Descripción de la evolución de la enfermedad actual..." data-id="element-931" />
                </div>
              </div>
            </FormSection>

            {/* Revisión por Sistemas */}
            <FormSection title="Revisión por Sistemas" data-id="element-932">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-933">
                <Input label="General" value={systemsReviewData.general} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                general: e.target.value
              })} data-id="element-934" />
                <Input label="Cardio-Respiratorio" value={systemsReviewData.cardioRespiratory} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                cardioRespiratory: e.target.value
              })} data-id="element-935" />
                <Input label="Gastro-Intestinal" value={systemsReviewData.gastrointestinal} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                gastrointestinal: e.target.value
              })} data-id="element-936" />
                <Input label="Genito-Urinario" value={systemsReviewData.genitourinary} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                genitourinary: e.target.value
              })} data-id="element-937" />
                <Input label="Osteo-Muscular" value={systemsReviewData.osteoMuscular} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                osteoMuscular: e.target.value
              })} data-id="element-938" />
                <Input label="Neurológico" value={systemsReviewData.neurological} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                neurological: e.target.value
              })} data-id="element-939" />
                <Input label="Piel & Anexos" value={systemsReviewData.skinAndAppendages} onChange={e => setSystemsReviewData({
                ...systemsReviewData,
                skinAndAppendages: e.target.value
              })} data-id="element-940" />
              </div>
            </FormSection>

            {/* Diagnóstico */}
            <FormSection title="Diagnóstico" data-id="element-941">
              <Textarea value={diagnosis} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.diagnosis) setDiagnosis(e.target.value);
            }} rows={6} placeholder="Escriba el diagnóstico..." maxLength={CHAR_LIMITS.diagnosis} data-id="element-942" />
              <p className={`mt-1 text-xs text-right ${diagnosis.length >= CHAR_LIMITS.diagnosis ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-943">
                {diagnosis.length}/{CHAR_LIMITS.diagnosis} caracteres
              </p>
            </FormSection>

            {/* Tratamiento */}
            <FormSection title="Tratamiento" data-id="element-944">
              <Textarea value={treatment} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.treatment) setTreatment(e.target.value);
            }} rows={10} placeholder="Escriba el tratamiento..." maxLength={CHAR_LIMITS.treatment} data-id="element-945" />
              <p className={`mt-1 text-xs text-right ${treatment.length >= CHAR_LIMITS.treatment ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-946">
                {treatment.length}/{CHAR_LIMITS.treatment} caracteres
              </p>
            </FormSection>

            {/* Indicaciones Médicas */}
            <FormSection title="Indicaciones Médicas" data-id="element-947">
              <Textarea value={medicalIndications} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.medicalIndications) setMedicalIndications(e.target.value);
            }} rows={6} placeholder="Indicaciones médicas..." maxLength={CHAR_LIMITS.medicalIndications} data-id="element-948" />
              <p className={`mt-1 text-xs text-right ${medicalIndications.length >= CHAR_LIMITS.medicalIndications ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-949">
                {medicalIndications.length}/{CHAR_LIMITS.medicalIndications}{' '}
                caracteres
              </p>
            </FormSection>

            {/* Sesiones */}
            <FormSection title="Sesiones" data-id="element-950">
              <div className="space-y-4" data-id="element-951">
                <div className="relative" data-id="element-952">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-953" />
                  <input type="text" value={sessionServiceSearch} onChange={e => setSessionServiceSearch(e.target.value)} placeholder="Buscar servicios..." className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-id="element-954" />
                </div>
                <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900/50" data-id="element-955">
                  {filteredSessionServices.map(svc => {
                  const isSelected = selectedSessionServiceIds.includes(svc.id);
                  return <label key={svc.id} className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 ${isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'hover:bg-white dark:hover:bg-gray-800'}`} data-id="element-956">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleSessionService(svc.id)} className="w-3.5 h-3.5 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500" data-id="element-957" />
                        <span className="text-sm text-gray-900 dark:text-white flex-1" data-id="element-958">
                          {svc.name}
                        </span>
                        <span className="text-xs text-gray-400" data-id="element-959">
                          {svc.duration} min
                        </span>
                      </label>;
                })}
                </div>
                {selectedSessionServiceIds.length > 0 && <div className="space-y-2" data-id="element-960">
                    {selectedSessionServiceIds.map(sId => {
                  const svc = services.find(s => s.id === sId);
                  if (!svc) return null;
                  return <div key={sId} className="flex items-center gap-3 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg" data-id="element-961">
                          <div className="flex-1 min-w-0" data-id="element-962">
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate block" data-id="element-963">
                              {svc.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-964">
                            <span className="text-xs text-gray-500" data-id="element-965">
                              Sesión N°
                            </span>
                            <input type="number" min="1" value={sessionServiceNumbers[sId] || '1'} onChange={e => setSessionServiceNumbers(prev => ({
                        ...prev,
                        [sId]: e.target.value
                      }))} className="w-16 px-2 py-1.5 text-sm text-center border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500" data-id="element-966" />
                            <button type="button" onClick={() => toggleSessionService(sId)} className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors" data-id="element-967">
                              <XIcon className="w-4 h-4" data-id="element-968" />
                            </button>
                          </div>
                        </div>;
                })}
                  </div>}
              </div>
            </FormSection>

            {/* Comentarios */}
            <FormSection title="Comentarios" data-id="element-969">
              <Textarea value={comments} onChange={e => {
              if (e.target.value.length <= CHAR_LIMITS.comments) setComments(e.target.value);
            }} rows={3} placeholder="Comentarios adicionales..." maxLength={CHAR_LIMITS.comments} data-id="element-970" />
              <p className={`mt-1 text-xs text-right ${comments.length >= CHAR_LIMITS.comments ? 'text-red-500 font-medium' : 'text-gray-400'}`} data-id="element-971">
                {comments.length}/{CHAR_LIMITS.comments} caracteres
              </p>
            </FormSection>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-gray-800 p-4 -mx-4 -mb-4 shadow-lg" data-id="element-972">
              <Button variant="outline" type="button" onClick={() => {
              setIsEditFormOpen(false);
              setEditingHistory(null);
              resetForm();
            }} data-id="element-973">
                Cancelar
              </Button>
              <Button type="submit" isLoading={isSaving} data-id="element-974">
                {isSaving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>

      {/* View Modal with Print-Optimized Layout */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Historial Clínico" size="full" data-id="element-975">
        {selectedHistory && <div className="print-clinical-history" data-id="element-976">
            {/* Print Header - repeats on every printed page via table-header-group */}
            <div className="print-header hidden print:block" data-id="element-977">
              <div className="print-header-inner" data-id="element-978">
                <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Logo" data-id="element-979" />
                <div data-id="element-980">
                  <h1 data-id="element-981">HISTORIA CLÍNICA</h1>
                  <p data-id="element-982">Savidna - Centro de Bienestar y Belleza</p>
                </div>
                <div className="print-date" data-id="element-983">
                  Fecha: {formatDate(selectedHistory.date)}
                </div>
              </div>
            </div>

            {/* Screen Header (visible on screen, hidden on print) */}
            <div className="print:hidden flex items-center justify-between pb-4 border-b-2 border-sky-500 mb-6" data-id="element-984">
              <div className="flex items-center gap-4" data-id="element-985">
                <img src="https://cdn.magicpatterns.com/uploads/a741bfNsYBMGGZNxniTt4s/images.jpg" alt="Savidna Logo" className="w-16 h-16 rounded-full" data-id="element-986" />
                <div data-id="element-987">
                  <h2 className="text-2xl font-bold text-sky-600 dark:text-sky-400" data-id="element-988">
                    HISTORIA CLÍNICA
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400" data-id="element-989">
                    Savidna - Centro de Bienestar y Belleza
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-990">
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-991">
                  Fecha:
                </p>
                <p className="font-semibold text-gray-900 dark:text-white" data-id="element-992">
                  {formatDate(selectedHistory.date)}
                </p>
              </div>
            </div>

            {/* Print Body - wraps all content sections */}
            <div className="print-body" data-id="element-993">
              {/* Patient Info Box */}
              <div className="print-patient-info mb-6" data-id="element-994">
                <div data-id="element-995">
                  <strong data-id="element-996">Paciente:</strong>{' '}
                  {selectedHistory.personalBackground.fullName}
                </div>
                <div data-id="element-997">
                  <strong data-id="element-998">Edad:</strong>{' '}
                  {selectedHistory.personalBackground.age} años
                </div>
                <div data-id="element-999">
                  <strong data-id="element-1000">Sexo:</strong>{' '}
                  {selectedHistory.personalBackground.gender}
                </div>
                <div data-id="element-1001">
                  <strong data-id="element-1002">Teléfono:</strong>{' '}
                  {selectedHistory.personalBackground.phone}
                </div>
                <div data-id="element-1003">
                  <strong data-id="element-1004">Email:</strong>{' '}
                  {selectedHistory.personalBackground.email}
                </div>
                <div data-id="element-1005">
                  <strong data-id="element-1006">Fecha:</strong> {formatDate(selectedHistory.date)}
                </div>
              </div>

              {/* Content sections */}
              <div className="space-y-6" data-id="element-1007">
                {/* Antecedentes Personales */}
                <div className="print-section" data-id="element-1008">
                  <h2 className="font-bold" data-id="element-1009">Antecedentes Personales</h2>
                  <div className="print-field-group" data-id="element-1010">
                    <div className="print-field" data-id="element-1011">
                      <div className="print-field-label font-bold" data-id="element-1012">
                        Ocupación:
                      </div>
                      <div className="print-field-value" data-id="element-1013">
                        {selectedHistory.personalBackground.occupation || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1014">
                      <div className="print-field-label font-bold" data-id="element-1015">
                        N° de hijos:
                      </div>
                      <div className="print-field-value" data-id="element-1016">
                        {selectedHistory.personalBackground.numberOfChildren}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1017">
                      <div className="print-field-label font-bold" data-id="element-1018">
                        Tipo de parto:
                      </div>
                      <div className="print-field-value" data-id="element-1019">
                        {selectedHistory.personalBackground.deliveryType || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1020">
                      <div className="print-field-label font-bold" data-id="element-1021">
                        Alcohol:
                      </div>
                      <div className="print-field-value" data-id="element-1022">
                        {selectedHistory.personalBackground.alcohol || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1023">
                      <div className="print-field-label font-bold" data-id="element-1024">Tabaco:</div>
                      <div className="print-field-value" data-id="element-1025">
                        {selectedHistory.personalBackground.tobacco || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1026">
                      <div className="print-field-label font-bold" data-id="element-1027">Drogas:</div>
                      <div className="print-field-value" data-id="element-1028">
                        {selectedHistory.personalBackground.drugs || '-'}
                      </div>
                    </div>
                    <div className="print-field print-field-full" data-id="element-1029">
                      <div className="print-field-label font-bold" data-id="element-1030">
                        Infusiones:
                      </div>
                      <div className="print-field-value" data-id="element-1031">
                        {selectedHistory.personalBackground.infusions || '-'}
                      </div>
                    </div>
                    <div className="print-field print-field-full" data-id="element-1032">
                      <div className="print-field-label font-bold" data-id="element-1033">
                        Motivo de la consulta:
                      </div>
                      <div className="print-field-value" data-id="element-1034">
                        {selectedHistory.personalBackground.consultationReason || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Antecedentes Clínicos */}
                <div className="print-section" data-id="element-1035">
                  <h2 className="font-bold" data-id="element-1036">Antecedentes Clínicos</h2>
                  <div className="space-y-3" data-id="element-1037">
                    <div className="print-field" data-id="element-1038">
                      <div className="print-field-label font-bold" data-id="element-1039">
                        Enfermedades:
                      </div>
                      <div className="print-field-value" data-id="element-1040">
                        {selectedHistory.clinicalBackground.diseases || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1041">
                      <div className="print-field-label font-bold" data-id="element-1042">
                        Medicamentos actuales:
                      </div>
                      <div className="print-field-value" data-id="element-1043">
                        {selectedHistory.clinicalBackground.currentMedications || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1044">
                      <div className="print-field-label font-bold" data-id="element-1045">
                        Enfermedades crónicas:
                      </div>
                      <div className="print-field-value" data-id="element-1046">
                        {selectedHistory.clinicalBackground.chronicDiseases || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1047">
                      <div className="print-field-label font-bold" data-id="element-1048">
                        Antecedentes quirúrgicos:
                      </div>
                      <div className="print-field-value" data-id="element-1049">
                        {selectedHistory.clinicalBackground.surgicalHistory || '-'}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1050">
                      <div className="print-field-label font-bold" data-id="element-1051">
                        Implantes/Dispositivos:
                      </div>
                      <div className="print-field-value" data-id="element-1052">
                        {selectedHistory.clinicalBackground.implantsOrDevices || '-'}
                        {selectedHistory.clinicalBackground.implantType && ` - Tipo: ${selectedHistory.clinicalBackground.implantType}`}
                      </div>
                    </div>
                    <div className="print-field" data-id="element-1053">
                      <div className="print-field-label font-bold" data-id="element-1054">
                        Alergias:
                      </div>
                      <div className="print-field-value" data-id="element-1055">
                        {selectedHistory.clinicalBackground.allergies || '-'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Exploración Física */}
                {selectedHistory.physicalExam && (selectedHistory.physicalExam.bloodPressure || selectedHistory.physicalExam.heartRate || selectedHistory.physicalExam.bloodSugar || selectedHistory.physicalExam.temperature || selectedHistory.physicalExam.weight || selectedHistory.physicalExam.height || selectedHistory.physicalExam.lungs || selectedHistory.physicalExam.eyes || selectedHistory.physicalExam.throat || selectedHistory.physicalExam.abdomen || (selectedHistory.physicalExam as any).positiveAspects || (selectedHistory.physicalExam as any).respiratoryRate || (selectedHistory.physicalExam as any).weightLb) && <div className="print-section" data-id="element-1056">
                      <h2 className="font-bold" data-id="element-1057">Exploración Física</h2>
                      <div className="print-field-group" data-id="element-1058">
                        {selectedHistory.physicalExam.bloodPressure && <div className="print-field" data-id="element-1059">
                            <div className="print-field-label font-bold" data-id="element-1060">
                              T.A. (Presión Arterial):
                            </div>
                            <div className="print-field-value" data-id="element-1061">
                              {selectedHistory.physicalExam.bloodPressure} mmHg
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.heartRate && <div className="print-field" data-id="element-1062">
                            <div className="print-field-label font-bold" data-id="element-1063">
                              F.C. (Frec. Cardíaca):
                            </div>
                            <div className="print-field-value" data-id="element-1064">
                              {selectedHistory.physicalExam.heartRate} Vm
                            </div>
                          </div>}
                        {(selectedHistory.physicalExam as any).respiratoryRate && <div className="print-field" data-id="element-1065">
                            <div className="print-field-label font-bold" data-id="element-1066">
                              F.R. (Frec. Respiratoria):
                            </div>
                            <div className="print-field-value" data-id="element-1067">
                              {(selectedHistory.physicalExam as any).respiratoryRate}{' '}
                              r/m
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.bloodSugar && <div className="print-field" data-id="element-1068">
                            <div className="print-field-label font-bold" data-id="element-1069">
                              Azúcar en Sangre:
                            </div>
                            <div className="print-field-value" data-id="element-1070">
                              {selectedHistory.physicalExam.bloodSugar} mg/dL
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.weight && <div className="print-field" data-id="element-1071">
                            <div className="print-field-label font-bold" data-id="element-1072">
                              Peso (Kg):
                            </div>
                            <div className="print-field-value" data-id="element-1073">
                              {selectedHistory.physicalExam.weight} kg
                            </div>
                          </div>}
                        {(selectedHistory.physicalExam as any).weightLb && <div className="print-field" data-id="element-1074">
                            <div className="print-field-label font-bold" data-id="element-1075">
                              Peso (Lb):
                            </div>
                            <div className="print-field-value" data-id="element-1076">
                              {(selectedHistory.physicalExam as any).weightLb}{' '}
                              lb
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.height && <div className="print-field" data-id="element-1077">
                            <div className="print-field-label font-bold" data-id="element-1078">
                              Altura:
                            </div>
                            <div className="print-field-value" data-id="element-1079">
                              {selectedHistory.physicalExam.height} cm
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.temperature && <div className="print-field" data-id="element-1080">
                            <div className="print-field-label font-bold" data-id="element-1081">
                              Temperatura:
                            </div>
                            <div className="print-field-value" data-id="element-1082">
                              {selectedHistory.physicalExam.temperature} °C
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.lungs && <div className="print-field" data-id="element-1083">
                            <div className="print-field-label font-bold" data-id="element-1084">
                              Pulmones:
                            </div>
                            <div className="print-field-value" data-id="element-1085">
                              {selectedHistory.physicalExam.lungs}
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.eyes && <div className="print-field" data-id="element-1086">
                            <div className="print-field-label font-bold" data-id="element-1087">
                              Ojos:
                            </div>
                            <div className="print-field-value" data-id="element-1088">
                              {selectedHistory.physicalExam.eyes}
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.throat && <div className="print-field" data-id="element-1089">
                            <div className="print-field-label font-bold" data-id="element-1090">
                              Garganta:
                            </div>
                            <div className="print-field-value" data-id="element-1091">
                              {selectedHistory.physicalExam.throat}
                            </div>
                          </div>}
                        {selectedHistory.physicalExam.abdomen && <div className="print-field" data-id="element-1092">
                            <div className="print-field-label font-bold" data-id="element-1093">
                              Abdomen:
                            </div>
                            <div className="print-field-value" data-id="element-1094">
                              {selectedHistory.physicalExam.abdomen}
                            </div>
                          </div>}
                        {(selectedHistory.physicalExam as any).positiveAspects && <div className="print-field print-field-full" data-id="element-1095">
                            <div className="print-field-label font-bold" data-id="element-1096">
                              Aspectos Positivos:
                            </div>
                            <div className="print-field-value" data-id="element-1097">
                              {(selectedHistory.physicalExam as any).positiveAspects}
                            </div>
                          </div>}
                      </div>
                    </div>}

                {/* Historia de la Enfermedad Actual */}
                {(selectedHistory as any).diseaseHistory && ((selectedHistory as any).diseaseHistory.sinceWhen || (selectedHistory as any).diseaseHistory.previousTreatment || (selectedHistory as any).diseaseHistory.evolution) && <div className="print-section" data-id="element-1098">
                      <h2 className="font-bold" data-id="element-1099">
                        Historia de la Enfermedad Actual
                      </h2>
                      <div className="print-field-group" data-id="element-1100">
                        {(selectedHistory as any).diseaseHistory.sinceWhen && <div className="print-field" data-id="element-1101">
                            <div className="print-field-label font-bold" data-id="element-1102">
                              ¿Desde cuándo padece el problema?
                            </div>
                            <div className="print-field-value" data-id="element-1103">
                              {(selectedHistory as any).diseaseHistory.sinceWhen}
                            </div>
                          </div>}
                        {(selectedHistory as any).diseaseHistory.previousTreatment && <div className="print-field" data-id="element-1104">
                            <div className="print-field-label font-bold" data-id="element-1105">
                              ¿Se ha tratado con anterioridad?
                            </div>
                            <div className="print-field-value" data-id="element-1106">
                              {(selectedHistory as any).diseaseHistory.previousTreatment}
                            </div>
                          </div>}
                        {(selectedHistory as any).diseaseHistory.evolution && <div className="print-field print-field-full" data-id="element-1107">
                            <div className="print-field-label font-bold" data-id="element-1108">
                              Evolución:
                            </div>
                            <div className="print-field-value whitespace-pre-wrap" data-id="element-1109">
                              {(selectedHistory as any).diseaseHistory.evolution}
                            </div>
                          </div>}
                      </div>
                    </div>}

                {/* Revisión por Sistemas */}
                {(selectedHistory as any).systemsReview && Object.values((selectedHistory as any).systemsReview).some(Boolean) && <div className="print-section" data-id="element-1110">
                      <h2 className="font-bold" data-id="element-1111">Revisión por Sistemas</h2>
                      <div className="print-field-group" data-id="element-1112">
                        {(selectedHistory as any).systemsReview.general && <div className="print-field" data-id="element-1113">
                            <div className="print-field-label font-bold" data-id="element-1114">
                              General:
                            </div>
                            <div className="print-field-value" data-id="element-1115">
                              {(selectedHistory as any).systemsReview.general}
                            </div>
                          </div>}
                        {(selectedHistory as any).systemsReview.cardioRespiratory && <div className="print-field" data-id="element-1116">
                            <div className="print-field-label font-bold" data-id="element-1117">
                              Cardio-Respiratorio:
                            </div>
                            <div className="print-field-value" data-id="element-1118">
                              {(selectedHistory as any).systemsReview.cardioRespiratory}
                            </div>
                          </div>}
                        {(selectedHistory as any).systemsReview.gastrointestinal && <div className="print-field" data-id="element-1119">
                            <div className="print-field-label font-bold" data-id="element-1120">
                              Gastro-Intestinal:
                            </div>
                            <div className="print-field-value" data-id="element-1121">
                              {(selectedHistory as any).systemsReview.gastrointestinal}
                            </div>
                          </div>}
                        {(selectedHistory as any).systemsReview.genitourinary && <div className="print-field" data-id="element-1122">
                            <div className="print-field-label font-bold" data-id="element-1123">
                              Genito-Urinario:
                            </div>
                            <div className="print-field-value" data-id="element-1124">
                              {(selectedHistory as any).systemsReview.genitourinary}
                            </div>
                          </div>}
                        {(selectedHistory as any).systemsReview.osteoMuscular && <div className="print-field" data-id="element-1125">
                            <div className="print-field-label font-bold" data-id="element-1126">
                              Osteo-Muscular:
                            </div>
                            <div className="print-field-value" data-id="element-1127">
                              {(selectedHistory as any).systemsReview.osteoMuscular}
                            </div>
                          </div>}
                        {(selectedHistory as any).systemsReview.neurological && <div className="print-field" data-id="element-1128">
                            <div className="print-field-label font-bold" data-id="element-1129">
                              Neurológico:
                            </div>
                            <div className="print-field-value" data-id="element-1130">
                              {(selectedHistory as any).systemsReview.neurological}
                            </div>
                          </div>}
                        {(selectedHistory as any).systemsReview.skinAndAppendages && <div className="print-field" data-id="element-1131">
                            <div className="print-field-label font-bold" data-id="element-1132">
                              Piel & Anexos:
                            </div>
                            <div className="print-field-value" data-id="element-1133">
                              {(selectedHistory as any).systemsReview.skinAndAppendages}
                            </div>
                          </div>}
                      </div>
                    </div>}

                {/* Diagnóstico */}
                <div className="print-section" data-id="element-1134">
                  <h2 className="font-bold" data-id="element-1135">Diagnóstico</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-1136">
                    {selectedHistory.diagnosis || '-'}
                  </div>
                </div>

                {/* Tratamiento */}
                <div className="print-section" data-id="element-1137">
                  <h2 className="font-bold" data-id="element-1138">Tratamiento</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-1139">
                    {selectedHistory.treatment || '-'}
                  </div>
                </div>

                {/* Indicaciones Médicas */}
                <div className="print-section" data-id="element-1140">
                  <h2 className="font-bold" data-id="element-1141">Indicaciones Médicas</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-1142">
                    {selectedHistory.medicalIndications || '-'}
                  </div>
                </div>

                {/* Sesiones */}
                <div className="print-section" data-id="element-1143">
                  <h2 className="font-bold" data-id="element-1144">Sesiones</h2>
                  {selectedHistory.sessionServices && selectedHistory.sessionServices.length > 0 ? <div className="space-y-2 mt-2" data-id="element-1145">
                      {selectedHistory.sessionServices.map((ss, idx) => <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700/50" data-id="element-1146">
                          <span className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-1147">
                            {ss.serviceName}
                          </span>
                          <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1148">
                            Sesión N°{' '}
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400" data-id="element-1149">
                              {ss.sessionNumber}
                            </span>
                          </span>
                        </div>)}
                    </div> : <div className="print-field-group" data-id="element-1150">
                      <div className="print-field" data-id="element-1151">
                        <div className="print-field-label font-bold" data-id="element-1152">
                          Sesiones:
                        </div>
                        <div className="print-field-value" data-id="element-1153">
                          {selectedHistory.sessions || '-'}
                        </div>
                      </div>
                      <div className="print-field" data-id="element-1154">
                        <div className="print-field-label font-bold" data-id="element-1155">
                          N° de sesión:
                        </div>
                        <div className="print-field-value" data-id="element-1156">
                          {selectedHistory.sessionNumber || '-'}
                        </div>
                      </div>
                    </div>}
                </div>

                {/* Comentarios */}
                <div className="print-section" data-id="element-1157">
                  <h2 className="font-bold" data-id="element-1158">Comentarios</h2>
                  <div className="print-field-value whitespace-pre-wrap" data-id="element-1159">
                    {selectedHistory.comments || '-'}
                  </div>
                </div>

                {/* Firma */}
                <div className="print-signature" data-id="element-1160">
                  <div className="print-signature-line" data-id="element-1161">
                    <p className="font-bold text-gray-900 dark:text-white" data-id="element-1162">
                      {selectedHistory.doctorSignature}
                    </p>
                  </div>
                  <p data-id="element-1163">Firma del Dr. (a)</p>
                </div>

                {/* Imágenes Adjuntas */}
                {selectedHistory.attachedImages && selectedHistory.attachedImages.length > 0 && <div className="print-section mt-8" data-id="element-1164">
                      <h2 className="font-bold" data-id="element-1165">Imágenes Adjuntas</h2>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-1166">
                        {selectedHistory.attachedImages.map((img, index) => <div key={index} className="relative group rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-1167">
                            <img src={img.data} alt={img.name || `Imagen ${index + 1}`} className="w-full h-auto max-h-96 object-contain bg-gray-50 dark:bg-gray-900 cursor-pointer" onClick={() => setViewingImage(img.data)} data-id="element-1168" />
                            <div className="p-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700" data-id="element-1169">
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate flex items-center gap-1.5" data-id="element-1170">
                                <ImageIcon className="w-3 h-3" data-id="element-1171" />
                                {img.name || `Imagen ${index + 1}`}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>}
              </div>

              {/* Actions (hidden on print) */}
              <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 no-print print:hidden" data-id="element-1172">
                <Button variant="outline" leftIcon={<FileDownIcon className="w-4 h-4" data-id="element-1174" />} onClick={() => selectedHistory && handleDownloadPdf(selectedHistory)} isLoading={isGeneratingPdf} data-id="element-1173">
                  {isGeneratingPdf ? 'Generando...' : 'Descargar PDF'}
                </Button>
                <Button onClick={() => setIsViewOpen(false)} data-id="element-1175">Cerrar</Button>
              </div>
            </div>
          </div>}
      </Modal>

      {/* Image Viewer Modal */}
      {viewingImage && <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setViewingImage(null)} data-id="element-1176">
          <div className="relative max-w-4xl max-h-[90vh] w-full" data-id="element-1177">
            <button onClick={() => setViewingImage(null)} className="absolute -top-12 right-0 p-2 text-white hover:text-gray-300 transition-colors" data-id="element-1178">
              <XIcon className="w-6 h-6" data-id="element-1179" />
            </button>
            <img src={viewingImage} alt="Vista ampliada" className="w-full h-full object-contain rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-1180" />
          </div>
        </div>}

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Historial' : 'Desactivar Historial'} size="sm" data-id="element-1181">
        <div className="space-y-4" data-id="element-1182">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1183">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar este historial clínico? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar este historial clínico? No aparecerá en las listas activas.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-1184">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-1185">
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleAction} data-id="element-1186">
              {actionType === 'archive' ? 'Archivar' : 'Desactivar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Historiales Clínicos" isExporting={isExporting} data-id="element-1187" />

      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Historiales Clínicos a PDF" isExporting={isPdfExporting} data-id="element-1188" />
    </div>;
}